import { defineStore } from 'pinia';
import type {
  IHttpResponse,
  ISceneSummaryControllerSystem,
  ISceneConfirmItem,
  IControllerGroup,
  IControlGroup,
  IDeviceGroup,
  IFaultNotificationData,
} from './definition';
import type { CCw2CloudPlanData } from '~~/store/pages/control-groups-register';

interface IConfirmModalProps {
  title: string;
  message: string;
  buttons: {
    text: string;
    onClick: () => void;
    id?: string;
    primary?: boolean;
    danger?: boolean;
  }[];
  closeType?: number;
  isPromise?: boolean;
  isScrollable?: boolean;
}

interface IPopupAlertModalProps {
  closeType?: number;
  startDate: number;
}

interface IInputModalProps {
  title: string;
  label: string;
  buttons: {
    text: string;
    onClick: (text: string, isEdit: boolean) => void;
    id?: string;
    required?: boolean;
    primary?: boolean;
    danger?: boolean;
  }[];
  defaultValue?: string;
  placeholder?: string;
  maxLength?: number;
}

export interface ISceneManagerRemotePlaybackModalProps {
  cloudControlGroup: IControlGroup;
  controllerGroup: Array<IControllerGroup>;
  sceneControllerSystems: Array<ISceneSummaryControllerSystem> | undefined;
  sceneName: string;
  isPlaying: boolean;
}
export interface ISceneManagerRegisterPreviewInfoProps {
  seekBarStartFrame: number;
  seekBarEndFrame: number;
  sceneStartFrame: number;
  sceneEndFrame: number;
}
export interface IColorPreviewGroupColor {
  chromaticityX: number;
  chromaticityY: number;
  brightness: number;
}

export interface IColorPreviewModalParams {
  deviceGroups: IDeviceGroup[];
  groupColors: Record<number, IColorPreviewGroupColor>;
  totalProductionTimeSec: number;
  fadeTimeSec: number;
}

export interface ISceneManagerRegisterPreviewProps {
  cloudControlGroupId: number;
  requestApiFunction: () => Promise<IHttpResponse>;
  sceneName: string;
  useSceneIds: number[];
  useContentIds: number[];
  previewInfo: ISceneManagerRegisterPreviewInfoProps;
  colorPreviewParams?: IColorPreviewModalParams;
  hiddenEvent?: () => void;
  cacheCloudPlanData?: CCw2CloudPlanData;
  cacheBackgroundImageBlobPath?: string;
}
export interface ISceneManagerSceneInfoModalProps {
  cloudControlGroupId: number;
  controllerSystems: Array<IControllerGroup>;
  sceneConfirmItem: ISceneConfirmItem;
}

interface IContentManagerModalProps {
  title: string;
}

interface ICalendarManagerListModalProps {
  mode: number;
  year: number;
  month: number;
  day: number;
}

interface IControlGroupsDeleteModalProps {
  isClearButtonInvisible: boolean;
}

export interface IDeviceManagerDetailProps {
  controllerId: string;
}
export interface IDeviceManagerSignalConverterStatusProps {
  controllerId: string;
}

export interface IDeviceManagerLightStatusProps {
  controllerId: string;
}

export interface IErrorHistoryInfoProps {
  errorInfoItem: IFaultNotificationData;
}

export const useModalStore = defineStore('modal', {
  state: () => {
    const { $MODAL_CLOSE_TYPE } = useNuxtApp();
    return {
      // 通常モーダル情報
      isConfirmModalOpen: false,
      confirmModalProps: null as IConfirmModalProps | null,

      // 入力モーダル情報
      isInputModalOpen: false,
      inputModalProps: null as IInputModalProps | null,

      // アラートモーダル情報
      isModalPopupAlertOpen: false,
      isModalMessageAlertOpen: false,
      popupAlertModalProps: null as IPopupAlertModalProps | null,

      // Promise用(await)
      isConfirmed: ref<(ans: boolean) => void>(() => {}),

      // Login
      isLoginChangePasswordModalOpen: false,

      // Control
      isControlGroupsDeleteModalOpen: false,
      controlGroupsDeleteModalProps: null as IControlGroupsDeleteModalProps | null,

      // Settings
      isSettingsAddNotificationModalOpen: false,

      // シーンプレビューモーダル情報
      isSceneManagerRegisterPreviewModalOpen: false,
      ISceneManagerRegisterPreviewProps: null as ISceneManagerRegisterPreviewProps | null,

      // リモート再生設定モーダル情報
      isSceneManagerRemotePlaybackModalOpen: false,
      ISceneManagerRemotePlaybackModalProps: null as ISceneManagerRemotePlaybackModalProps | null,

      // シーン情報モーダル情報
      isSceneManagerSceneInfoModalOpen: false,
      ISceneManagerSceneInfoModalProps: null as ISceneManagerSceneInfoModalProps | null,

      // Data Transceiver
      isDataTransceiverModalOpen: false,

      // Content Manager
      isContentManagerCommonModalOpen: ref(false),
      contentManagerModalProps: null as IContentManagerModalProps | null,

      // Calendar Manager
      isCalendarManagerListModalOpen: false,
      calendarManagerListModalProps: null as ICalendarManagerListModalProps | null,

      // Device Manager
      isDeviceManagerDetailsModalOpen: false,
      IDeviceManagerDetailProps: null as IDeviceManagerDetailProps | null,
      isDeviceManagerLightConditionModalOpen: false,
      IDeviceManagerLightStatusProps: null as IDeviceManagerLightStatusProps | null,
      isDeviceManagerSignalConverterStatusModalOpen: false,
      IDeviceManagerSignalConverterStatusProps:
        null as IDeviceManagerSignalConverterStatusProps | null,

      // 異常履歴情報
      isErrorHistoryInfoModalOpen: false,
      IErrorHistoryInfoProps: null as IErrorHistoryInfoProps | null,

      // Modalの上にConfirm Modalが表示されているかフラグ
      isConfirmModalOnModal: false,

      // Modalを閉じる際の挙動
      closeType: $MODAL_CLOSE_TYPE.ALL,
    };
  },
  getters: {
    // いずれかのモーダルが表示されているか確認する
    isAnyModalOpen(state) {
      return (
        state.isConfirmModalOpen ||
        state.isInputModalOpen ||
        state.isModalMessageAlertOpen ||
        state.isLoginChangePasswordModalOpen ||
        state.isControlGroupsDeleteModalOpen ||
        state.isSettingsAddNotificationModalOpen ||
        state.isSceneManagerRegisterPreviewModalOpen ||
        state.isSceneManagerRemotePlaybackModalOpen ||
        state.isSceneManagerSceneInfoModalOpen ||
        state.isDataTransceiverModalOpen ||
        state.isContentManagerCommonModalOpen ||
        state.isCalendarManagerListModalOpen ||
        state.isDeviceManagerDetailsModalOpen ||
        state.isDeviceManagerLightConditionModalOpen ||
        state.isDeviceManagerSignalConverterStatusModalOpen ||
        state.isModalPopupAlertOpen
      );
    },
  },
  actions: {
    // 通常モーダルを開く
    openConfirmModal(props: IConfirmModalProps, close: boolean = true) {
      if (close) {
        this.closeAll();
      }
      this.confirmModalProps = props;
      this.isConfirmModalOpen = true;
    },

    // 非同期でモーダルを開く
    async openConfirmModalPromise(props: IConfirmModalProps, close: boolean = true) {
      props = props = { ...props, isPromise: true };
      this.openConfirmModal(props, close);
      return new Promise<boolean>((resolve) => {
        this.isConfirmed = resolve;
      });
    },

    // 入力モーダルを開く
    openInputModal(props: IInputModalProps) {
      this.closeAll();
      this.inputModalProps = props;
      this.isInputModalOpen = true;
    },

    // ポップアラートモーダルを開く
    openPopupAlertModal(props: IPopupAlertModalProps) {
      this.isModalPopupAlertOpen = true;
      this.popupAlertModalProps = props;
    },

    // メッセージアラートモーダルを開く
    openModalMessageAlert() {
      this.closeAll();
      this.isModalMessageAlertOpen = true;
    },

    // Login
    openLoginChangePasswordModal() {
      this.closeAll();
      this.isLoginChangePasswordModalOpen = true;
    },

    // Control
    openControlGroupsDeleteModal(props: IControlGroupsDeleteModalProps) {
      this.closeAll();
      this.isControlGroupsDeleteModalOpen = true;
      this.controlGroupsDeleteModalProps = props;
    },

    // Settings
    openSettingsAddNotificationModal() {
      this.closeAll();
      this.isSettingsAddNotificationModalOpen = true;
    },

    // Scene Manager
    //  シーンプレビュー
    openSceneManagerRegisterPreviewModal(props: ISceneManagerRegisterPreviewProps) {
      this.closeAll();
      this.ISceneManagerRegisterPreviewProps = props;
      this.isSceneManagerRegisterPreviewModalOpen = true;
    },

    //  リモート再生設定
    openSceneManagerRemotePlaybackModal(props: ISceneManagerRemotePlaybackModalProps) {
      this.closeAll();
      this.ISceneManagerRemotePlaybackModalProps = props;
      this.isSceneManagerRemotePlaybackModalOpen = true;
    },

    //  シーン情報設定
    openSceneManagerSceneInfoModal(props: ISceneManagerSceneInfoModalProps) {
      this.closeAll();
      this.ISceneManagerSceneInfoModalProps = props;
      this.isSceneManagerSceneInfoModalOpen = true;
    },

    // Data Transceiver
    openDataTransceiverModal() {
      this.closeAll();
      this.isDataTransceiverModalOpen = true;
    },

    // Content Manager
    openContentManagerCommonModal(props: IContentManagerModalProps) {
      this.closeAll();
      this.contentManagerModalProps = props;
      this.isContentManagerCommonModalOpen = true;

      // console.log(
      //   'props.title : ' +
      //     props.title +
      //     ', props.isClearButtonInvisible : ' +
      //     props.isClearButtonInvisible
      // );
    },

    // Calendar Manager
    //  カレンダー一覧
    openCalendarManagerListModal(props: ICalendarManagerListModalProps) {
      this.closeAll();
      this.calendarManagerListModalProps = props;
      this.isCalendarManagerListModalOpen = true;
    },

    // Device Manager
    //  詳細
    openDeviceManagerDetailsModal(props?: IDeviceManagerDetailProps) {
      if (props) {
        this.IDeviceManagerDetailProps = props;
      }
      this.closeAll();
      this.isDeviceManagerDetailsModalOpen = true;
    },
    //  照明器具状態
    openDeviceManagerLightConditionModal(props: IDeviceManagerLightStatusProps) {
      this.IDeviceManagerLightStatusProps = props;
      this.closeAll();
      this.isDeviceManagerLightConditionModalOpen = true;
    },
    // 信号変換状態
    openDeviceManagerSignalConverterStatusModal(props: IDeviceManagerSignalConverterStatusProps) {
      this.IDeviceManagerSignalConverterStatusProps = props;
      this.closeAll();
      this.isDeviceManagerSignalConverterStatusModalOpen = true;
    },

    //  異常履歴
    openErrorHistoryInfoModal(props: IErrorHistoryInfoProps) {
      this.closeAll();
      this.IErrorHistoryInfoProps = props;
      this.isErrorHistoryInfoModalOpen = true;
    },

    // 画面外、×ボタンを押下した際の処理
    onClose(closeType: number | undefined, isPromise: boolean | undefined) {
      const { $MODAL_CLOSE_TYPE } = useNuxtApp();

      if (isPromise) this.isConfirmed(false);

      if (!closeType) {
        this.closeAll();
        return;
      }

      switch (closeType) {
        case $MODAL_CLOSE_TYPE.ALL:
          this.closeAll();
          break;

        case $MODAL_CLOSE_TYPE.CONFIRM:
          this.closeConfirmModal();
          break;

        case $MODAL_CLOSE_TYPE.POPUP_ALERT:
          this.closePopupAlertModal();
          break;

        default:
          this.closeAll();
          break;
      }
    },

    // モーダルをすべて閉じる
    closeAll() {
      // Common
      this.isConfirmModalOpen = false;
      this.confirmModalProps = null;

      // Input
      this.isInputModalOpen = false;
      this.inputModalProps = null;

      // Alert
      this.isModalPopupAlertOpen = false;
      this.isModalMessageAlertOpen = false;

      // Login
      this.isLoginChangePasswordModalOpen = false;

      // Control
      this.isControlGroupsDeleteModalOpen = false;

      // Settings
      this.isSettingsAddNotificationModalOpen = false;

      // Scene Manager
      this.isSceneManagerRegisterPreviewModalOpen = false;
      this.isSceneManagerRemotePlaybackModalOpen = false;
      this.isSceneManagerSceneInfoModalOpen = false;

      // Data Transceiver
      this.isDataTransceiverModalOpen = false;

      // Content Manager
      this.isContentManagerCommonModalOpen = false;

      // Calendar Manager
      this.isCalendarManagerListModalOpen = false;

      // Device Manager
      this.isDeviceManagerDetailsModalOpen = false;
      this.isDeviceManagerLightConditionModalOpen = false;
      this.isDeviceManagerSignalConverterStatusModalOpen = false;

      // Operation Status
      this.isErrorHistoryInfoModalOpen = false;
    },

    //通常モーダルを閉じる
    closeConfirmModal() {
      this.isConfirmModalOpen = false;
      this.confirmModalProps = null;
      this.isConfirmModalOnModal = false;
    },

    // ポップアラートモーダルを閉じる
    closePopupAlertModal() {
      this.isModalPopupAlertOpen = false;
    },

    // プレビューモーダルを閉じる
    closePreviewModal() {
      this.isSceneManagerRegisterPreviewModalOpen = false;
    },
  },
});
